/**
 * Turbo Cache Cleanup Composable
 * 
 * Provides automatic cleanup of Bootstrap components before Turbo Drive
 * caches the page. This prevents stale component instances and Popper.js
 * attributes from being cached, which would break components after navigation.
 * 
 * Follows the stimulus-use composable pattern.
 * 
 * @param {Controller} controller - The Stimulus controller instance
 * @param {Object} options - Configuration options
 * @param {Function} options.cleanup - Function to call before cache (required)
 * @param {Function} options.initialize - Function to call after render (optional)
 * 
 * @example Basic usage with cleanup only
 * import { useTurboCache } from '../utils/use_turbo_cache'
 * 
 * export default class extends Controller {
 *   connect() {
 *     this.initPopovers()
 *     useTurboCache(this, { cleanup: this.destroyPopovers.bind(this) })
 *   }
 *   
 *   initPopovers() { ... }
 *   destroyPopovers() { ... }
 * }
 * 
 * @example With both cleanup and reinitialize
 * import { useTurboCache } from '../utils/use_turbo_cache'
 * 
 * export default class extends Controller {
 *   connect() {
 *     this.initDropdowns()
 *     useTurboCache(this, {
 *       cleanup: this.cleanupDropdowns.bind(this),
 *       initialize: this.initDropdowns.bind(this)
 *     })
 *   }
 * }
 */
export function useTurboCache(controller, options = {}) {
  const { cleanup, initialize } = options
  
  if (!cleanup) {
    console.warn('useTurboCache: cleanup function is required')
    return
  }

  // Store bound handlers on the controller for cleanup
  const boundCleanup = cleanup
  const boundInitialize = initialize

  // Handler for turbo:before-cache - clean up before page is cached
  const beforeCacheHandler = () => {
    boundCleanup()
  }

  // Handler for turbo:render - reinitialize after cache restore (optional)
  const renderHandler = () => {
    if (boundInitialize) {
      boundInitialize()
    }
  }

  // Add event listeners
  document.addEventListener('turbo:before-cache', beforeCacheHandler)
  if (boundInitialize) {
    document.addEventListener('turbo:render', renderHandler)
  }

  // Store original disconnect to chain with cleanup
  const originalDisconnect = controller.disconnect?.bind(controller)

  // Override disconnect to clean up event listeners
  controller.disconnect = () => {
    document.removeEventListener('turbo:before-cache', beforeCacheHandler)
    if (boundInitialize) {
      document.removeEventListener('turbo:render', renderHandler)
    }
    
    // Call cleanup on disconnect too
    boundCleanup()
    
    // Call original disconnect if it existed
    if (originalDisconnect) {
      originalDisconnect()
    }
  }

  // Return cleanup function for manual use if needed
  return {
    cleanup: boundCleanup,
    initialize: boundInitialize
  }
}

/**
 * Helper to clean up Bootstrap component instances
 * 
 * @param {Element} container - Container element to search within
 * @param {string} selector - CSS selector for trigger elements
 * @param {Function} getInstanceFn - Bootstrap's getInstance function for the component
 * 
 * @example
 * cleanupBootstrapInstances(
 *   this.element,
 *   '[data-bs-toggle="dropdown"]',
 *   window.bootstrap.Dropdown.getInstance
 * )
 */
export function cleanupBootstrapInstances(container, selector, getInstanceFn) {
  if (!container || !getInstanceFn) return
  
  container.querySelectorAll(selector).forEach(el => {
    const instance = getInstanceFn(el)
    if (instance) {
      // Hide first if the method exists (for popovers, tooltips, dropdowns)
      if (typeof instance.hide === 'function') {
        instance.hide()
      }
      instance.dispose()
    }
  })
}

/**
 * Helper to initialize Bootstrap components with stale instance cleanup
 * 
 * @param {Element} container - Container element to search within
 * @param {string} selector - CSS selector for trigger elements  
 * @param {Function} ComponentClass - Bootstrap component class (e.g., window.bootstrap.Dropdown)
 * @param {Object} options - Options to pass to the Bootstrap component constructor
 * @param {Function} getTarget - Optional function to get target element from trigger (for Collapse)
 * 
 * @example Dropdown
 * initBootstrapComponents(
 *   this.element,
 *   '[data-bs-toggle="dropdown"]',
 *   window.bootstrap.Dropdown
 * )
 * 
 * @example Tooltip with options
 * initBootstrapComponents(
 *   this.element,
 *   '[data-bs-toggle="tooltip"]',
 *   window.bootstrap.Tooltip,
 *   { html: true, sanitize: false }
 * )
 */
export function initBootstrapComponents(container, selector, ComponentClass, options = {}, getTarget = null) {
  if (!container || !ComponentClass) return
  
  container.querySelectorAll(selector).forEach(triggerEl => {
    // Determine the target element (usually the trigger itself, except for Collapse)
    const targetEl = getTarget ? getTarget(triggerEl) : triggerEl
    if (!targetEl) return
    
    // Clean up any stale instance first
    const existingInstance = ComponentClass.getInstance(targetEl)
    if (existingInstance) {
      existingInstance.dispose()
    }
    
    // Create fresh instance
    new ComponentClass(targetEl, options)
  })
}

